/**
 * New onboarding template (plan docs/plans/lms-plan.md 12.1, 18.24; WP-47;
 * R14): a name (unique in the network, case-insensitively), an optional
 * description and whether it is the default (the one "Start onboarding"
 * pre-selects; making it the default clears the old default). A duplicate
 * name is 409 carrying existing_template { id, is_active }: the dialog says
 * so and offers to open that template. The new template opens in the editor,
 * where its items are added.
 */
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import Modal from '../../../../ui/Modal';
import { useToast } from '../../../../ui/Toast';
import { fieldCls, primaryBtnCls, secondaryBtnCls } from '../../../../ui/styles';
import { createOnboardingTemplate, existingTemplateOf, ONBOARDING_LIMITS } from '../../../api/onboarding';
import { checkboxCls, ErrorNote, labelCls } from '../../../components/pickers/shared';
import { describeOnboardingError } from '../../onboarding/shared';

export interface NewTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Open a template's editor (the new one, or the existing one a duplicate name names). */
  onOpenTemplate: (templateId: string) => void;
}

export function NewTemplateModal({ isOpen, onClose, onOpenTemplate }: NewTemplateModalProps) {
  const { toast } = useToast();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existingId, setExistingId] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setName('');
    setDescription('');
    setIsDefault(false);
    setSaving(false);
    setError(null);
    setExistingId(null);
  }, [isOpen]);

  const trimmed = name.trim();
  const nameProblem = trimmed.length > ONBOARDING_LIMITS.templateName ? `A name is at most ${ONBOARDING_LIMITS.templateName} characters.` : null;
  const descriptionProblem =
    description.trim().length > ONBOARDING_LIMITS.templateDescription
      ? `A description is at most ${ONBOARDING_LIMITS.templateDescription} characters.`
      : null;
  const canSave = trimmed.length > 0 && !nameProblem && !descriptionProblem && !saving;

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!canSave) return;
    setSaving(true);
    setError(null);
    setExistingId(null);
    try {
      const template = await createOnboardingTemplate({ name: trimmed, description: description.trim() || null, is_default: isDefault });
      toast(`Created ${template.name}. Add its items next.`, { type: 'success' });
      onOpenTemplate(template.id);
    } catch (err) {
      const existing = existingTemplateOf(err);
      setExistingId(existing ? existing.id : null);
      setError(describeOnboardingError(err, 'The template could not be created.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={saving ? () => {} : onClose} title="New onboarding template" maxWidth="max-w-lg">
      <form onSubmit={submit} className="space-y-4" data-testid="onboarding-template-new-form">
        <div>
          <label htmlFor="lms-onboarding-template-name" className={labelCls}>
            Name
          </label>
          <input
            id="lms-onboarding-template-name"
            className={`${fieldCls} min-h-[44px] w-full sm:min-h-0`}
            value={name}
            maxLength={ONBOARDING_LIMITS.templateName + 20}
            onChange={(e) => setName(e.target.value)}
            data-testid="onboarding-template-new-name"
          />
          {nameProblem ? <p className="mt-1 text-xs text-red-700 dark:text-red-400">{nameProblem}</p> : null}
        </div>
        <div>
          <label htmlFor="lms-onboarding-template-description" className={labelCls}>
            Description <span className="font-normal text-gray-500 dark:text-gray-400">(optional)</span>
          </label>
          <textarea
            id="lms-onboarding-template-description"
            className={`${fieldCls} w-full`}
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            data-testid="onboarding-template-new-description"
          />
          {descriptionProblem ? <p className="mt-1 text-xs text-red-700 dark:text-red-400">{descriptionProblem}</p> : null}
        </div>
        <label className="flex min-h-[44px] cursor-pointer items-center gap-3 text-sm text-gray-800 dark:text-gray-200 sm:min-h-0">
          <input
            type="checkbox"
            className={checkboxCls}
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
            data-testid="onboarding-template-new-default"
          />
          Make this the default template (pre-selected when onboarding starts)
        </label>

        {error ? (
          <ErrorNote testId="onboarding-template-new-error">
            <p>{error}</p>
            {existingId ? (
              <button
                type="button"
                className={`${secondaryBtnCls} mt-2`}
                onClick={() => onOpenTemplate(existingId)}
                data-testid="onboarding-template-open-existing"
              >
                Open the existing template
              </button>
            ) : null}
          </ErrorNote>
        ) : null}

        <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-4 dark:border-gray-700">
          <button type="button" className={secondaryBtnCls} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className={primaryBtnCls} disabled={!canSave} data-testid="onboarding-template-new-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
            Create template
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default NewTemplateModal;
